import { Calculator, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';

import { WEAK_SPECIES, PH_PROBLEMS, WEAK_PROBLEMS } from './acidBaseCalculationsData';
import {
  DEFAULT_ACID_BASE_EXPLORE,
  STRONG_PH_CHALLENGES,
  STRONG_PH_QUESTION,
  STRONG_SOLUTES,
  WEAK_PH_CHALLENGES,
  WEAK_PH_QUESTION,
  acidBaseExploreFields,
  calculateAcidBaseExplore,
  exploreSetupFromProblem,
  type AcidBaseExploreKind,
  type AcidBaseExploreSetup,
} from './acidBaseExplore';
import { IonBars, IonisationGrid, PhStrip, StrengthLadder } from './AcidBaseCalculationsVisuals';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { useLastValidResult } from './useLastValidResult';

const KIND_LABELS: Record<AcidBaseExploreKind, string> = {
  strong: 'One strong solution',
  dilution: 'Dilute it',
  mixing: 'Mix an acid and a base',
  weak: 'Weak acid or base',
};

const SLIDER_BY_KIND: Partial<Record<AcidBaseExploreKind, 'finalMl' | 'baseVolumeMl'>> = {
  dilution: 'finalMl',
  mixing: 'baseVolumeMl',
};

const SELECT_CLASS =
  'mt-1 h-11 w-full rounded-2xl border border-hairline bg-canvas-pure px-3 text-base font-extrabold text-ink';

function SoluteSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { id: string; label: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <label className="block text-sm font-extrabold text-ink">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={SELECT_CLASS}>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const strongOptions = (type?: 'acid' | 'base') =>
  STRONG_SOLUTES.filter((solute) => !type || solute.type === type).map((solute) => ({
    id: solute.id,
    label: `${solute.formula} (strong ${solute.type})`,
  }));
const WEAK_OPTIONS = WEAK_SPECIES.map((species) => ({
  id: species.id,
  label: `${species.formula} · ${species.type === 'acid' ? 'Ka' : 'Kb'} ${species.k.toExponential(1)}`,
}));

/** Explore: the learner's own concentrations and volumes, the worked pH and linked views. */
export function AcidBaseCalculationsExplore({ mode }: { mode: 'ph' | 'weak' }) {
  const kinds: AcidBaseExploreKind[] = mode === 'ph' ? ['strong', 'dilution', 'mixing'] : ['weak'];
  const [setup, setSetup] = useState<AcidBaseExploreSetup>(() => ({ ...DEFAULT_ACID_BASE_EXPLORE, kind: kinds[0] }));
  const [presetCount, setPresetCount] = useState(0);
  const { result, error } = useLastValidResult(setup, calculateAcidBaseExplore);
  const presets = (mode === 'ph' ? PH_PROBLEMS : WEAK_PROBLEMS).flatMap((problem) => {
    const loaded = exploreSetupFromProblem(problem.spec, setup);
    return loaded ? [{ id: problem.id, title: problem.title, setup: loaded }] : [];
  });
  const species = result?.weakSpeciesId ? WEAK_SPECIES.find((candidate) => candidate.id === result.weakSpeciesId) : undefined;
  const change = (patch: Partial<AcidBaseExploreSetup>) => setSetup((current) => ({ ...current, ...patch }));

  return (
    <section aria-label="Acid–base explorer" className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sky text-ink">
          <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Explore · your own numbers</p>
          <h2 className="text-xl font-extrabold text-ink">Type a solution and watch the pH follow</h2>
        </div>
      </div>

      {kinds.length > 1 ? (
        <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Calculation type">
          {kinds.map((kind) => (
            <button
              key={kind}
              type="button"
              aria-pressed={setup.kind === kind}
              onClick={() => change({ kind })}
              className={`rounded-full border px-3 py-1.5 text-sm font-extrabold ${
                setup.kind === kind ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
              }`}
            >
              {KIND_LABELS[kind]}
            </button>
          ))}
        </div>
      ) : null}

      {presets.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Load numbers from a reviewed problem">
          <span className="text-xs font-extrabold uppercase tracking-wide text-slate2">Shortcuts</span>
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                setSetup(preset.setup);
                setPresetCount((count) => count + 1);
              }}
              className="rounded-full border border-hairline bg-white px-3 py-1 text-xs font-bold text-slate2 hover:border-brand-sky/40"
            >
              {preset.title}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {setup.kind === 'strong' || setup.kind === 'dilution' ? (
              <SoluteSelect label="Strong solute" value={setup.strongId} options={strongOptions()} onChange={(strongId) => change({ strongId })} />
            ) : null}
            {setup.kind === 'mixing' ? (
              <>
                <SoluteSelect label="Acid" value={setup.acidId} options={strongOptions('acid')} onChange={(acidId) => change({ acidId })} />
                <SoluteSelect label="Base" value={setup.baseId} options={strongOptions('base')} onChange={(baseId) => change({ baseId })} />
              </>
            ) : null}
            {setup.kind === 'weak' ? (
              <SoluteSelect label="Weak acid or base (25 °C)" value={setup.weakId} options={WEAK_OPTIONS} onChange={(weakId) => change({ weakId })} />
            ) : null}
          </div>
          <ModelInputPanel
            key={`${setup.kind}-${presetCount}`}
            legend="Your solution"
            fields={acidBaseExploreFields(setup.kind)}
            values={setup.values}
            onChange={(values) => change({ values })}
            sliderKey={SLIDER_BY_KIND[setup.kind]}
          />
          <ExploreChallenges
            question={mode === 'ph' ? STRONG_PH_QUESTION : WEAK_PH_QUESTION}
            challenges={mode === 'ph' ? STRONG_PH_CHALLENGES : WEAK_PH_CHALLENGES}
            result={result && !error ? { setup, result } : null}
          />
          {error ? (
            <p role="alert" className="rounded-2xl bg-wash-sunshine px-3 py-2 text-sm font-bold text-ink">
              {error} The model keeps the last valid result.
            </p>
          ) : null}
          {result ? (
            <WorkedStepsPanel
              title="How the model works it out"
              lead={result.lead}
              leadTestId="acid-base-lead"
              steps={result.steps}
              stepTestIdPrefix="acid-base-step"
              result={result.summary}
              resultTestId="acid-base-result"
              limitNote={result.limitNote}
            />
          ) : null}
        </div>

        <div className="min-w-0 space-y-5 rounded-[1.5rem] border border-hairline bg-surface p-4 sm:p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <Calculator className="h-4 w-4" aria-hidden="true" /> Model view
          </p>
          {result && result.modelPH !== null ? (
            <>
              <PhStrip modelPH={result.modelPH} learnerPH={null} />
              <IonBars modelPH={result.modelPH} />
            </>
          ) : (
            <p className="rounded-2xl border-2 border-dashed border-hairline px-4 py-6 text-center text-sm font-bold text-slate2">
              The model stops at its limit, so no pH is drawn.
            </p>
          )}
          {result && result.percentIonised !== null && species ? (
            <IonisationGrid percent={result.percentIonised} formula={species.formula} />
          ) : null}
          {setup.kind === 'weak' ? (
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">Reviewed Ka values, 25 °C</p>
              <StrengthLadder highlightId={species?.type === 'acid' ? species.id : null} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
